import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Backend, PERMISSION} from '@generated-backend-types';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {takeUntil} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {DiagnosisService} from '../../../../services/api/diagnosis.service';
import {parseTimeParts} from '../../../../utils/time';
import {EmailService} from '../../../../services/api/administration/email.service';
import {
  DateTimeSelectorComponent,
  TIME_MODE
} from '../../../../components/form-inputs/date-time-selector/date-time-selector.component';
import {
  MultiSelectEnumComponent
} from '../../../../components/form-inputs/multi-select-enum/multi-select-enum.component';
import {EditModeEnum} from '../../../../enums/EditModeEnum';
import {DeleteCardComponent} from '../../../../components/form-inputs/delete-card/delete-card.component';
import {DateTime} from 'luxon';
import {FormSubmitService} from '../../../../services/form-submit.service';
import {TimeTrackingEntryService} from '../../../../services/api/time-tracking/time-tracking-entry.service';
import {UserSessionService} from '../../../../services/user-session.service';
import {ToastrService} from 'ngx-toastr';
import TimeTrackingEntry = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingEntry;
import TimeTrackingEntryData = Backend.App.Data.Dashboard.TimeTracking.TimeTrackingEntryData;
import TimeTrackingEntryTypeEnum = Backend.App.Enums.TimeTracking.TimeTrackingEntryTypeEnum;

@Component({
  selector: 'app-authenticated-time-tracking-entries-edit',
  templateUrl: './edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormGroupComponent,
    DateTimeSelectorComponent,
    MultiSelectEnumComponent,
    DeleteCardComponent,
  ],
  standalone: true,
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public entry?: TimeTrackingEntry;
  public mode = EditModeEnum.CREATE;

  public form: StrictFormGroup<TimeTrackingEntryData> = this.fb.group({
    start_at: this.fb.control(new Date().toISOString(), [Validators.required]),
    end_at: this.fb.control(new Date().toISOString(), [Validators.required]),
    type: this.fb.control(TimeTrackingEntryTypeEnum.WORK, [Validators.required]),
    break_time: this.fb.control(null, [Validators.pattern(/^\d+$/)]),
    comment: this.fb.control(''),
  });
  protected readonly TimeTrackingEntryTypeEnum = TimeTrackingEntryTypeEnum;
  protected readonly TIME_MODE = TIME_MODE;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    protected readonly diagnosisService: DiagnosisService,
    protected emailService: EmailService,
    private readonly formSubmitService: FormSubmitService,
    private readonly timeTrackingEntryService: TimeTrackingEntryService,
    private readonly router: Router,
    private readonly userSessionService: UserSessionService,
    private readonly toastrService: ToastrService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const entry: TimeTrackingEntry | undefined = data['entry'];

      if (entry) {
        this.entry = entry;
        this.mode = EditModeEnum.EDIT;

        this.setBreakTimeControlEnabledState(entry.type);

        this.form.patchValue(entry);
        this.cdr.markForCheck();
      } else {
        this.userSessionService.getStoredUserInfo().subscribe(user => {
          const timeTrackingEmployee = user?.time_tracking_employee;
          if (!timeTrackingEmployee) return;

          if (timeTrackingEmployee.default_time_tracking_entry_start_time) {
            this.setTime(this.form.controls.start_at, timeTrackingEmployee.default_time_tracking_entry_start_time);
          }

          if (timeTrackingEmployee.default_time_tracking_entry_end_time) {
            this.setTime(this.form.controls.end_at, timeTrackingEmployee.default_time_tracking_entry_end_time);
          }
        });
      }

      this.form.controls.type.valueChanges.subscribe(value => this.setBreakTimeControlEnabledState(value));

      this.titleService.setTitle(`authenticated.time_tracking.entries.${this.mode}.title`);
    });
  }

  public save() {
    this.formSubmitService.submit<TimeTrackingEntry, TimeTrackingEntryData, TimeTrackingEntryService, TimeTrackingEntryData>({
      form: this.form,
      service: this.timeTrackingEntryService,
      modelId: this.entry?.id,
      createPermission: PERMISSION.TIMETRACKING_TIMETRACKINGENTRY_CREATE_ALL,
      updatePermission: PERMISSION.TIMETRACKING_TIMETRACKINGENTRY_UPDATE_ALL,
      createSuccessMessage: this.translate.instant('authenticated.time_tracking.entries.create.success'),
      updateSuccessMessage: this.translate.instant('authenticated.time_tracking.entries.edit.success'),
      createErrorMessage: this.translate.instant('authenticated.time_tracking.entries.create.error'),
      updateErrorMessage: this.translate.instant('authenticated.time_tracking.entries.edit.error'),
      dataTransformer: (data) => {
        return {
          type: data.type,
          start_at: DateTime.fromISO(data.start_at ?? '').toJSDate().toISOString(),
          end_at: DateTime.fromISO(data.end_at ?? '').toJSDate().toISOString(),
          break_time: data.break_time,
          comment: data.comment,
        };
      }
    }).subscribe(entry => {
      if (!entry) return;
      this.entry = entry;

      if (this.mode === EditModeEnum.CREATE) {
        this.router.navigateByUrl(`/time-tracking/entries/${this.entry.id}`).then();
      }

      this.mode = this.entry?.id ? EditModeEnum.EDIT : EditModeEnum.CREATE;
      this.cdr.markForCheck();
    })
  }

  public delete() {
    if (this.mode !== EditModeEnum.EDIT || !this.entry) return;

    if (!this.userSessionService.hasAllPermission([PERMISSION.TIMETRACKING_TIMETRACKINGENTRY_DELETE_ALL])) {
      this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
      return;
    }

    this.timeTrackingEntryService.delete(this.entry.id).subscribe({
      next: () => {
        this.toastrService.success(this.translate.instant('authenticated.time_tracking.entries.delete.success'));
        this.router.navigateByUrl('/time-tracking/entries').then();
      },
      error: () => {
        this.toastrService.error(this.translate.instant('authenticated.time_tracking.entries.delete.error'));
      }
    });
  }

  private setTime(control: FormControl<string|null>, time: string): void {
    const {hour, minute} = parseTimeParts(time);
    const date = DateTime.fromISO(control.value ?? '').toJSDate()

    date.setHours(hour);
    date.setMinutes(minute);

    control.setValue(date.toISOString());
  }

  private setBreakTimeControlEnabledState(type: TimeTrackingEntryTypeEnum): void {
    switch (type) {
      case TimeTrackingEntryTypeEnum.WORK:
        this.form.controls.break_time.enable();
        this.form.controls.break_time.markAsUntouched();
        this.form.controls.break_time.markAsPristine();
        break;
      case TimeTrackingEntryTypeEnum.SICK:
      case TimeTrackingEntryTypeEnum.CHILD_SICK:
      case TimeTrackingEntryTypeEnum.EDUCATIONAL_LEAVE:
      case TimeTrackingEntryTypeEnum.OFF_DAY:
      case TimeTrackingEntryTypeEnum.OVERTIME_OFF:
        this.form.controls.break_time.setValue(null);
        this.form.controls.break_time.disable();
    }
  }
}
